import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { getKata, type KataId } from '../data/catalog'
import {
  getKataScoring,
  isScoringSupported,
  type MistakeSeverity,
  type SuggestedMistake,
} from '../data/scoring'
import { localizeCriterion } from '../data/scoring/localize'
import {
  emptyElementMark,
  scoreElement,
  scorePerformance,
  setCriterionSeverity,
  setElementComment,
  setElementForgotten,
  type ElementMarkState,
} from '../lib/scoring/engine'
import { kataBannerBackground } from '../lib/theme/kataBanner'
import { LanguageSwitcher } from '../components/i18n/LanguageSwitcher'
import {
  ScoringCriteriaSourceNotice,
  ScoringDisclaimer,
} from '../components/scoring/ScoringDisclaimer'
import { useI18n } from '../i18n'
import type { MessageKey } from '../i18n/messages'
import {
  completePerformance,
  createPerformance,
  getPerformance,
  savePerformance,
} from '../storage/repositories/performances'
import type { KataPerformance } from '../storage/schema'
import styles from './KataScoring.module.css'

function formatScore(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

function suggestedKey(kind: SuggestedMistake): MessageKey {
  switch (kind) {
    case 'small':
      return 'scoring.kindSmall'
    case 'medium':
      return 'scoring.kindMedium'
    case 'small_medium':
      return 'scoring.kindSmallMedium'
    case 'big':
      return 'scoring.kindBig'
    case 'forgotten':
      return 'scoring.kindForgotten'
  }
}

export function KataJudge() {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const { kataId } = useParams<{ kataId: string }>()
  const [searchParams] = useSearchParams()
  const performanceId = searchParams.get('performance')
  const elementFromQuery = searchParams.get('element')

  const kata = kataId ? getKata(kataId as KataId) : undefined
  const scoring = kata ? getKataScoring(kata.id) : undefined

  const [performance, setPerformance] = useState<KataPerformance | null | undefined>(undefined)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showComplete, setShowComplete] = useState(false)
  const [scoredBy, setScoredBy] = useState('')
  const [note, setNote] = useState('')
  const [englishCriterionIds, setEnglishCriterionIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const completeDialogRef = useRef<HTMLDialogElement>(null)
  const completeTitleId = useId()

  useEffect(() => {
    if (locale === 'en') setEnglishCriterionIds(new Set())
  }, [locale])

  useEffect(() => {
    const dialog = completeDialogRef.current
    if (!dialog) return
    if (showComplete) {
      if (!dialog.open) dialog.showModal()
    } else if (dialog.open) {
      dialog.close()
    }
  }, [showComplete])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!kata || !scoring) return
      try {
        if (performanceId) {
          const existing = await getPerformance(performanceId)
          if (cancelled) return
          if (!existing || existing.kataId !== kata.id) {
            setError(true)
            setPerformance(null)
            return
          }
          setPerformance(existing)
          setScoredBy(existing.scoredBy ?? '')
          setNote(existing.note ?? '')
          return
        }
        const created = await createPerformance(kata.id)
        if (cancelled) return
        navigate(`/kata/${kata.id}/judge?performance=${created.id}`, { replace: true })
      } catch {
        if (!cancelled) {
          setError(true)
          setPerformance(null)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [kata, scoring, performanceId, navigate])

  // Restore element from Watch-reference return URL, or start at the first element.
  // Kept separate from the load effect so re-fetches do not clobber the rail selection.
  useEffect(() => {
    if (!scoring || !performanceId) return
    if (elementFromQuery) {
      const fromElement = scoring.elements.find((e) => e.id === elementFromQuery)
      if (fromElement) {
        setSelectedId(fromElement.id)
        return
      }
    }
    setSelectedId((current) =>
      current && scoring.elements.some((e) => e.id === current)
        ? current
        : (scoring.elements[0]?.id ?? null),
    )
  }, [scoring, performanceId, elementFromQuery])

  const readOnly = performance?.status === 'completed'

  const marksById = useMemo(() => {
    const map = new Map<string, ElementMarkState>()
    for (const m of performance?.elementMarks ?? []) map.set(m.elementId, m)
    return map
  }, [performance])

  const liveScore = useMemo(() => {
    if (!scoring || !performance) return null
    return scorePerformance(scoring.elements, performance.elementMarks, scoring.maxPerJudge)
  }, [scoring, performance])

  const selected = scoring?.elements.find((e) => e.id === selectedId)
  const selectedMark = selectedId ? marksById.get(selectedId) : undefined
  const selectedScore = selected ? scoreElement(selected, selectedMark) : null

  const updateMark = useCallback(
    (elementId: string, updater: (mark: ElementMarkState) => ElementMarkState) => {
      setPerformance((current) => {
        if (!current || current.status === 'completed') return current
        const existing = current.elementMarks.find((m) => m.elementId === elementId)
        const base = existing ?? emptyElementMark(elementId)
        const updated = updater({ ...base })
        const rest = current.elementMarks.filter((m) => m.elementId !== elementId)
        const next: KataPerformance = {
          ...current,
          elementMarks: [...rest, updated],
          updatedAt: new Date().toISOString(),
        }
        const scored = scoring
          ? scorePerformance(scoring.elements, next.elementMarks, scoring.maxPerJudge)
          : null
        const withTotals: KataPerformance = scored
          ? {
              ...next,
              rawTotal: scored.rawTotal,
              forgottenPenaltyApplied: scored.forgottenPenaltyApplied,
              finalTotal: scored.finalTotal,
              maxTotal: scored.maxTotal,
            }
          : next
        void savePerformance(withTotals).catch(() => setError(true))
        return withTotals
      })
    },
    [scoring],
  )

  const selectedIndex = scoring?.elements.findIndex((e) => e.id === selectedId) ?? -1

  async function handleComplete() {
    if (!performance || !liveScore || readOnly) return
    const unreviewed = liveScore.elementCount - liveScore.reviewedCount
    if (unreviewed > 0) {
      const ok = window.confirm(t('scoring.unreviewedWarn', { count: unreviewed }))
      if (!ok) return
    }
    setSaving(true)
    try {
      const saved = await completePerformance(performance, {
        scoredBy: scoredBy.trim() || undefined,
        note: note.trim() || undefined,
      })
      setPerformance(saved)
      setShowComplete(false)
      navigate(`/kata/${performance.kataId}/performances`)
    } catch {
      setError(true)
    } finally {
      setSaving(false)
    }
  }

  if (!kata) return <Navigate to="/" replace />
  if (!isScoringSupported(kata.id) || !scoring) {
    return (
      <div className="app-shell">
        <p className="banner banner-info">{t('scoring.unsupported')}</p>
        <Link to={`/kata/${kata.id}`} className="btn btn-secondary">
          {t('scoring.backPractice')}
        </Link>
      </div>
    )
  }

  if (performance === undefined) {
    return (
      <div className="app-shell">
        <p className="banner banner-info">{t('common.loading')}</p>
      </div>
    )
  }

  if (performance === null) {
    return (
      <div className="app-shell">
        <p className="banner banner-error" role="alert">
          {t('scoring.loadError')}
        </p>
        <Link to={`/kata/${kata.id}/performances`} className="btn btn-secondary">
          {t('scoring.backPerformances')}
        </Link>
      </div>
    )
  }

  const rail = (horizontal: boolean) => (
    <ul className={`${styles.railList} ${horizontal ? styles.railHorizontal : ''}`}>
      {scoring.elements.map((el) => {
        const mark = marksById.get(el.id)
        const scored = scoreElement(el, mark)
        const isSelected = el.id === selectedId
        return (
          <li key={el.id}>
            <button
              type="button"
              className={`${styles.railChip} ${isSelected ? styles.railChipSelected : ''}`}
              aria-pressed={isSelected}
              onClick={() => setSelectedId(el.id)}
            >
              <span lang={el.nameJa ? (locale === 'en' ? undefined : 'ja') : undefined}>
                {locale === 'en' ? el.name : el.nameJa ?? el.name}
              </span>
              <span className={styles.railScore}>
                {mark?.reviewed || mark?.forgotten ? formatScore(scored.points) : '—'}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )

  return (
    <div className="app-shell">
      <header className={styles.topBar}>
        <div className={styles.topRow}>
          <Link
            to={`/kata/${kata.id}/performances`}
            className={`btn btn-ghost ${styles.topBack}`}
          >
            {t('scoring.backPerformances')}
          </Link>
          <LanguageSwitcher compact className={styles.topLang} />
        </div>
        <h1 className={styles.pageTitle}>
          <span lang="ja">{kata.nameJa}</span>
          <span className={styles.pageRomaji}>{kata.name}</span>
        </h1>
      </header>

      <div
        className={styles.hero}
        style={{
          background: kataBannerBackground(kata.theme),
          color: kata.theme.text,
        }}
      >
        <p className={styles.heroLabel}>{t('scoring.judgeLabel')}</p>
        <p className={styles.heroTitle} lang={selected?.nameJa ? 'ja' : undefined}>
          {selected
            ? locale === 'en'
              ? selected.name
              : selected.nameJa ?? selected.name
            : '—'}
        </p>
        {selected && locale !== 'en' ? (
          <p className={styles.heroMeta}>{selected.name}</p>
        ) : null}
        {readOnly ? <p className={styles.heroMeta}>{t('scoring.readOnly')}</p> : null}
      </div>

      <ScoringCriteriaSourceNotice />

      {error ? (
        <p className="banner banner-error" role="alert">
          {t('scoring.loadError')}
        </p>
      ) : null}

      {liveScore ? (
        <div className={styles.scoreBar} aria-live="polite">
          <span className={styles.scoreTotal}>
            {t('scoring.scoreBar', {
              total: formatScore(liveScore.finalTotal),
              max: liveScore.maxTotal,
            })}
          </span>
          <span>
            {t('scoring.reviewed', {
              reviewed: liveScore.reviewedCount,
              total: liveScore.elementCount,
            })}
          </span>
          <span
            className={`banner ${liveScore.forgottenPenaltyApplied ? 'banner-warning' : 'banner-info'}`}
            style={{ margin: 0, padding: '4px 10px' }}
          >
            {liveScore.forgottenPenaltyApplied
              ? t('scoring.halfPenaltyOn')
              : t('scoring.halfPenaltyOff')}
          </span>
          {!readOnly ? (
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginLeft: 'auto' }}
              disabled={saving}
              onClick={() => setShowComplete(true)}
            >
              {t('scoring.complete')}
            </button>
          ) : (
            <Link
              to={`/kata/${kata.id}/performances`}
              className="btn btn-secondary"
              style={{ marginLeft: 'auto' }}
            >
              {t('scoring.backPerformances')}
            </Link>
          )}
        </div>
      ) : null}

      <div className={styles.workspace}>
        <aside className={`surface ${styles.rail} ${styles.desktopOnly}`}>
          <h2 className={styles.sectionTitle}>{t('scoring.elements')}</h2>
          {rail(false)}
        </aside>

        <div className={styles.mobileOnly}>
          <h2 className={styles.sectionTitle}>{t('scoring.elements')}</h2>
          {rail(true)}
        </div>

        <section className={`surface ${styles.pane}`}>
          {selected && selectedScore ? (
            <>
              <div className={styles.paneHeader}>
                <h2 className={styles.paneTitle} lang={selected.nameJa ? 'ja' : undefined}>
                  {locale === 'en' ? selected.name : selected.nameJa ?? selected.name}
                </h2>
                <span className={styles.panePoints}>
                  {formatScore(selectedScore.points)} / 10
                </span>
              </div>
              <p className={styles.chartHint}>{t('scoring.mistakesOnElement')}</p>

              {locale !== 'en' ? (
                <p className={styles.translationDisclaimer} role="note">
                  {t('scoring.translationDisclaimer')}
                </p>
              ) : null}

              <ul className={styles.criteria}>
                {selected.criteria.map((criterion) => {
                  const applied = selectedMark?.criteria.find(
                    (c) => c.criterionId === criterion.id,
                  )?.severity
                  const showEnglish =
                    locale !== 'en' && englishCriterionIds.has(criterion.id)
                  const criteriaLocale = showEnglish ? 'en' : locale
                  const localized = localizeCriterion(kata.id, criterion, criteriaLocale)
                  return (
                    <li key={criterion.id} className={styles.criterion}>
                      <div className={styles.criterionHeader}>
                        <p
                          className={styles.criterionText}
                          lang={localized.translated ? locale : 'en'}
                        >
                          {localized.text}
                        </p>
                        {locale !== 'en' ? (
                          <button
                            type="button"
                            className={styles.criteriaLangBtn}
                            aria-pressed={showEnglish}
                            onClick={() => {
                              setEnglishCriterionIds((prev) => {
                                const next = new Set(prev)
                                if (next.has(criterion.id)) next.delete(criterion.id)
                                else next.add(criterion.id)
                                return next
                              })
                            }}
                          >
                            {showEnglish
                              ? t('scoring.showTranslatedCriteria')
                              : t('scoring.showEnglishCriteria')}
                          </button>
                        ) : null}
                      </div>
                      <span className={styles.suggested}>
                        {t('scoring.suggested', {
                          kind: t(suggestedKey(criterion.suggested)),
                        })}
                        {localized.hint ? ` — ${localized.hint}` : ''}
                      </span>
                      <div
                        className={styles.severityRow}
                        role="group"
                        aria-label={localized.text}
                      >
                        {(['small', 'medium', 'big'] as MistakeSeverity[]).map((sev) => (
                          <button
                            key={sev}
                            type="button"
                            className={`${styles.severityBtn} ${
                              sev === 'medium'
                                ? styles.severityMedium
                                : sev === 'big'
                                  ? styles.severityBig
                                  : ''
                            }`}
                            aria-pressed={applied === sev}
                            disabled={readOnly || selectedMark?.forgotten}
                            onClick={() => {
                              updateMark(selected.id, (mark) =>
                                setCriterionSeverity(
                                  mark,
                                  criterion.id,
                                  applied === sev ? null : sev,
                                ),
                              )
                            }}
                          >
                            {sev === 'small'
                              ? t('scoring.severitySmall')
                              : sev === 'medium'
                                ? t('scoring.severityMedium')
                                : t('scoring.severityBig')}
                          </button>
                        ))}
                      </div>
                    </li>
                  )
                })}
              </ul>

              <div className={styles.forgotten}>
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(selectedMark?.forgotten)}
                    disabled={readOnly}
                    onChange={(e) => {
                      updateMark(selected.id, (mark) =>
                        setElementForgotten(mark, e.target.checked),
                      )
                    }}
                  />
                  <span>{t('scoring.forgotten')}</span>
                </label>
                <p className={styles.forgottenHint}>{t('scoring.forgottenHint')}</p>
              </div>

              <label className={`${styles.field} ${styles.elementComment}`}>
                <span>{t('scoring.elementComment')}</span>
                <textarea
                  value={selectedMark?.comment ?? ''}
                  onChange={(e) => {
                    updateMark(selected.id, (mark) =>
                      setElementComment(mark, e.target.value),
                    )
                  }}
                  rows={3}
                  disabled={readOnly}
                  placeholder={t('scoring.elementCommentPlaceholder')}
                  maxLength={4000}
                />
                <span className={styles.forgottenHint}>{t('scoring.elementCommentHint')}</span>
              </label>

              <p className={styles.chartHint} aria-live="polite">
                {t('scoring.applied', { points: formatScore(selectedScore.points) })}
              </p>

              <div className={styles.navRow}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={selectedIndex <= 0}
                  onClick={() => {
                    const prev = scoring.elements[selectedIndex - 1]
                    if (prev) setSelectedId(prev.id)
                  }}
                >
                  {t('scoring.prevElement')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={selectedIndex < 0 || selectedIndex >= scoring.elements.length - 1}
                  onClick={() => {
                    const next = scoring.elements[selectedIndex + 1]
                    if (next) setSelectedId(next.id)
                  }}
                >
                  {t('scoring.nextElement')}
                </button>
                {selected.techniqueId ? (
                  <Link
                    to={`/kata/${kata.id}?${new URLSearchParams({
                      technique: selected.techniqueId,
                      ...(performance
                        ? {
                            fromPerformance: performance.id,
                            fromElement: selected.id,
                          }
                        : {}),
                    }).toString()}`}
                    className="btn btn-ghost"
                  >
                    {t('scoring.watchReference')}
                  </Link>
                ) : null}
              </div>
            </>
          ) : null}
        </section>
      </div>

      <ScoringDisclaimer />

      <dialog
        ref={completeDialogRef}
        className={styles.completeDialog}
        aria-labelledby={completeTitleId}
        onClose={() => setShowComplete(false)}
        onCancel={(event) => {
          event.preventDefault()
          setShowComplete(false)
        }}
      >
        <div className={styles.completeSheet}>
          <h3 id={completeTitleId}>{t('scoring.completeTitle')}</h3>
          {liveScore ? (
            <p className={styles.completeSummary}>
              {t('scoring.scoreBar', {
                total: formatScore(liveScore.finalTotal),
                max: liveScore.maxTotal,
              })}
              {' · '}
              {t('scoring.reviewed', {
                reviewed: liveScore.reviewedCount,
                total: liveScore.elementCount,
              })}
            </p>
          ) : null}
          <label className={styles.field}>
            <span>{t('scoring.scoredBy')}</span>
            <input
              value={scoredBy}
              onChange={(e) => setScoredBy(e.target.value)}
              autoComplete="name"
            />
          </label>
          <label className={styles.field}>
            <span>{t('scoring.note')}</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </label>
          <div className={styles.navRow}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowComplete(false)}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || readOnly}
              onClick={() => void handleComplete()}
            >
              {t('scoring.saveComplete')}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  )
}
